import {computed} from 'vue'
import {useRoute} from 'vue-router'
import {LocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/enum/LocaleType'
import {AppPlatformType} from '@toolify/server/src/models/mongoose/SessionModel/enum/AppPlatformType'
import {i18n} from '@toolify/client/src/setup/i18n'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {useAppStore} from '@toolify/client/src/stores/AppStore/useAppStore'
import {useCurrentAppPlatform} from '@toolify/client/src/modules/platform/composables/useCurrentAppPlatform'
import {useGuestLocaleSwitch} from '@toolify/client/src/modules/internalization/composables/useGuestLocaleSwitch'
import {findPreferredLocaleType} from '@toolify/client/src/modules/internalization/functions/findPreferredLocaleType'

function getBrowserLanguageTags(): readonly string[] {
  if(typeof navigator === 'undefined') {
    return []
  }
  return navigator.languages?.length ? navigator.languages : [navigator.language]
}

/**
 * The browser language to offer a signed-out web visitor whose page is in another language, or null.
 * Only languages with a translation file count, so a visitor is never sent to a page that falls back
 * to English anyway.
 */
export function useGuestLocaleSuggestion() {
  const route = useRoute()
  const authStore = useAuthStore()
  const appStore = useAppStore()
  const {currentAppPlatformType} = useCurrentAppPlatform()
  const {chosenLocale} = useGuestLocaleSwitch()
  const browserLocale = findPreferredLocaleType(getBrowserLanguageTags(), i18n.global.availableLocales)

  const suggestedLocale = computed<LocaleType | null>(() => {
    if(currentAppPlatformType !== AppPlatformType.Web || !browserLocale) {
      return null
    }
    if(!appStore.isFirstRouteInitialized || authStore.isLoggedIn) {
      return null
    }
    const authMeta = route.meta.auth
    if(authMeta?.required && authMeta.value !== false) {
      return null
    }
    const currentLocale = i18n.global.locale.value
    if(browserLocale === currentLocale || chosenLocale.value === currentLocale) {
      return null
    }
    return browserLocale
  })

  return {
    suggestedLocale,
  }
}
